import { React, Styled } from '@common/3rd';
import { DeskComponentRegistry, CheckableWidget } from '@component';
import { Utils } from '@common';
import { CodeItem, CodesFilter, CheckableWidgetProps } from '@my-types'
import { StyledComponents } from '@styles';
type DeskTableProps = {
	title: any[];
	rows: Array<Array<string | number>>;
	selectedTitle?: string | string[];
	note?: string;
	codes?: CodeItem[];
	codesFilter?: CodesFilter;
};
type DeskTableState = {

};
type DeskTableComponents = {
	Box: any;
	Headers: any;
	Header: any;
	Contents: any;
	Content: any;
	SubTitle: any;
	Radio: any;
	Note: any;
};

/**
 * 静态样式表格
 */
class DeskTable<
	P extends CheckableWidgetProps & DeskTableProps,
	S extends DeskTableState,
	C extends DeskTableComponents
	> extends CheckableWidget<P, S, C> {
	radioRef: React.RefObject<any>;
	radioRefs: HTMLInputElement[];
	constructor(props: any, context?: any) {
		super(props, context);
		this.radioRef = React.createRef();
		this.radioRefs = [];
	}
	componentDidMount() {
		super.componentDidMount();
		(this.radioRefs as HTMLInputElement[]).forEach(ref => {
			ref.checked = this.getValueFromModel() == ref.value;
		})
	}
	private setRef = (ref: any) => {
		!this.radioRefs.includes(ref) && this.radioRefs.push(ref);
	}
	protected doAfterCheckCompleted(): this {
		return this;
	}
	protected initComponents(): C {
		return {
			Box: Styled.div.attrs({
				'data-widget': 'desk-table'
			})`
				display: flex;
				flex-direction: column;
				max-width: 100vw;
				border: solid 1px;
				border-color: lightgrey;
				border-radius: 4px;
				${props => this.renderCSS(props.theme)}
			`,
			Header: Styled.div.attrs({
				'data-widget': 'desk-tab-header'
			})`
				width: 100%;
				display: grid;
				position: relative;
				border-bottom: ${props => props.theme.BORDER};
				background-color: ${props => props.theme.COLOR_PRIMARY};
				${props => this.renderCSS(props.theme)}
				grid-template-columns: ${(props: any) =>
					new Array(props.count).fill('1fr').join(' ')};
				div {
					text-align: center;
					word-break: keep-all;
					color: white;
					&[data-disabled=true] {
						display: none;
					}
				}
			`,
			Contents: Styled.div.attrs({
				'data-widget': 'desk-tab-contents'
			})`
				width: 100%;
				display: grid;
				position: relative;
				grid-template-columns: ${(props: any) =>
					new Array(props.count).fill('1fr').join(' ')};
				&:not(:last-child) {
					border-bottom: 1px solid #ddd;
				}
			`,
			Content: Styled.div.attrs({
				'data-widget': 'desk-tab-content'
			})`
				height: auto;
				width: 100%;
				position: relative;
				text-align: center;
				div {
					white-space: pre-line;
				}
				&:not(:first-child) {
					border-left: 1px solid #ddd;
				}
				&[data-disabled=true] {
					display: none;
				}
			`,
			SubTitle: Styled.div.attrs({
				'data-widget': 'desk-tab-sub-title'
			})`
				height: auto;
				width: 100%;
				position: relative;
				text-align: center;
				display: grid;
				grid-template-columns: ${(props: any) =>
					new Array(props.count).fill('1fr').join(' ')};
				background: #ddd;
				div {
					white-space: pre-line;
				}
			`,
			Note: Styled.div.attrs({
				'data-widget': 'desk-table-note'
			})``,
			Radio: Styled(StyledComponents.RADIO)`
				display: inline-block;
				height: inherit;
				&:checked, &:not(:checked) {
					position: absolute;
    				left: -9999px;
				}
				&:checked + label,:not(:checked) + label {
					position: relative;
					padding-left: 28px;
					cursor: pointer;
					line-height: 20px;
					display: inline-block;
				}
				&:checked + label:before,
				&:not(:checked) + label:before {
					content: '';
					position: absolute;
					left: 0;
					top: 0;
					width: 18px;
					height: 18px;
					border: 1px solid #ddd;
					border-radius: 100%;
					background: #fff;
				}
				&:checked + label:after,
				&:not(:checked) + label:after {
					content: '';
					width: 12px;
					height: 12px;
					background: ${props => props.theme.COLOR_SUCCESS};
					position: absolute;
					top: 3px;
					left: 3px;
					border-radius: 100%;
					transition: all 0.2s ease;
				}
				&:not(:checked) + label:after {
					opacity: 0;
					transform: scale(0);
				}
				&:checked + label:after {
					opacity: 1;
					transform: scale(1);
				}
			`
		} as C;
	}
	focus(): this {
		this.radioRefs[0].focus()
		return this;
	}
	protected setValueToDOM(value: any, propName?: string): this {
		if (propName == null || propName === this.getPropName()) {
			this.radioRefs.forEach(ref => {
				ref && (ref.checked =  value === ref.value);
			})
		}
		return this;
	}
	private onValueChanged = (): void => {
		const checked = this.radioRefs.find(ref => ref && ref.checked);
		const value = checked?.value; // this.radioRef.current.value;
		const oldValue = this.getValueFromModel();
		if (value === oldValue) {
			// 没有变化, 什么都不做
			return;
		}

		this.setValueToModel(value).checkData();
	};
	getCodes(): CodeItem[] {
		const codesFilter = this.getCodesFilter();
		if (codesFilter) {
			if (Utils.isFunction(codesFilter)) {
				return codesFilter.call(this, this.props.codes, this);
			} else {
				return codesFilter.filter.call(this, this.props.codes, this);
			}
		} else {
			return Utils.toArray(this.props.codes);
		}
	}
	protected getCodesFilter(): CodesFilter | undefined {
		return this.props.codesFilter;
	}
	protected renderTableHeader(): JSX.Element {
		const { Header, Radio } = this.getComponents();
		return (
			<Header key={'table-header' + Math.ceil(Math.random() * 1000)} count={this.getTitle().length}>
				{this.getTitle().map((title: string, index: number) => {
					return (
						<div key={index}
							 data-disabled={this.props.codes && index !== 0 && !this.getCodes().some(code => this.props.codes && (code.v == this.props.codes[index  - 1]?.v))}>
							{this.props.codes && index !== 0 ? 
							<><Radio key={index + this.getPropName()}
									 id={this.props.codes[index  - 1]?.l}
									 name={this.getPropName()}
									 value={this.props.codes[index  - 1]?.v || ''}
									 onChange={this.onValueChanged}
									 ref={this.setRef}
							/>
							<label htmlFor={this.props.codes[index  - 1]?.l}>{title}</label></> : 
							title}
						</div>
					);
				})}
			</Header>
		);
	}
	protected renderChildren(): any {
		const { Contents, SubTitle } = this.getComponents();
		if (this.getRows().length > 0) {
			return this.getRows().map((row: any, index: number) => {
				row = row.filter((t:any)=>t)
				if (row.length > 1) {
					// count 跟着title走
					return <Contents key={'contents' + index + Math.ceil(Math.random() * 1000)} count={this.getTitle().length}>
						{this.renderRowItems(row)}
					</Contents>
				} else if (row.length === 1) {
					// 如果数组只有一个，则表示子标题
					return (
						<SubTitle key={'sub-title' + Math.ceil(Math.random() * 1000)} count={this.getTitle().length}>
							<div>{row[0]}</div>
						</SubTitle>
					);
				} else {
					return;
				}
			})
		} else {
			return;
		}
	}

	private renderRowItems(row: Array<string | number>): any {
		const { Content } = this.getComponents();
		if (row.length > 0) {
			return row.map((item: any, index: number) => {
				return (
					<Content key={'item' + index + Math.ceil(Math.random() * 1000)} count={row.length}
							 data-disabled={this.props.codes && index !== 0 && !this.getCodes().some(code => this.props.codes && (code.v == this.props.codes[index  - 1]?.v))}
					>
						<div>{item}</div>
					</Content>
				);
			});
		} else {
			return;
		}
	}
	renderNote() {
		const { Note } = this.getComponents();
		return this.props.note ? <Note>{this.props.note}</Note> : null
	}
	render() {
		const { Box } = this.getComponents();
		return <>
			<Box>
				{this.renderTableHeader()}
				{this.renderChildren()}
			</Box>
			{this.renderNote()}
		</>
		;
	}
	protected getTitle(): string[] {
		let title: string[] = this.props.title || [];
		if (this.props.selectedTitle) {
			let selectedTitle = Utils.isString(this.props.selectedTitle) ? [this.props.selectedTitle] : this.props.selectedTitle as string[];
			if (selectedTitle.length && this.props.title.length) {
				title = [this.props.title[0]].concat(this.props.title.filter(t => selectedTitle.some(st => t.includes(st))));
			}
		}
		return title;
	}
	protected getRows(): Array<Array<string | number>> {
		let rows = this.props.rows || [];
		if (this.props.selectedTitle) {
			let selectedTitle: string[] = Utils.isString(this.props.selectedTitle) ? [this.props.selectedTitle] : this.props.selectedTitle as string[];
			let indexList = selectedTitle.reduce((res:any, val:any)=>{
				let selectedIndex = this.props.title.findIndex((obj:any) => obj.includes(val));
				selectedIndex && res.push(selectedIndex);
				return res;
			}, []);
			rows = this.props.rows.map(row => {
				return (indexList as number[]).reduce((arr, i) => {
					arr.push(row[i]);
					return arr;
				}, [row[0]]);
			});
		}
		return rows;
	}
}

DeskComponentRegistry.register({
	table: DeskTable
});

export { DeskTable };
